import { FaHardHat, FaTools, FaWrench, FaRulerCombined, FaShieldAlt, FaChartLine, FaMoneyBill, FaPeopleCarry, FaArrowUp } from "react-icons/fa";
import { images } from '../constants'; 

const careerIntro = {
    sub: "Build Your Future",
    heading: "Join Our Construction Team Today",
    description: "At BuildX, we’re looking for skilled and motivated individuals to be part of our growing team. Whether you're experienced in construction or eager to learn, explore exciting career opportunities in building, project management, and site operations.",
    points: [
        {
            icon: <FaHardHat />,
            title: "Opportunities in General Construction",
        },
        {
            icon: <FaTools />,
            title: "Assist in Residential & Commercial Projects",
        },
        {
            icon: <FaWrench />,
            title: "Hands-on Training & Development Programs",
        },
        {
            icon: <FaRulerCombined />,
            title: "Site Planning & Project Execution",
        },
        {
            icon: <FaShieldAlt />,
            title: "Work in a Safety-Focused Environment",
        },
    ],
};

const benefits = [
    {
        id: 1,
        icon: <FaChartLine />,
        title: "Career Advancement",
        description: "Grow your career in the construction industry with opportunities for promotion, leadership roles, and skill enhancement.",
    },
    {
        id: 2,
        icon: <FaHardHat />,
        title: "Hands-on Training",
        description: "Receive expert training in construction techniques, safety procedures, and project management to develop your skills.",
    },
    {
        id: 3,
        icon: <FaMoneyBill />,
        title: "Competitive Pay",
        description: "Earn competitive wages with performance-based incentives, ensuring financial growth and job stability.",
    },
    {
        id: 4,
        icon: <FaShieldAlt />,
        title: "Job Security",
        description: "Work in a stable and growing industry with consistent job opportunities and long-term career prospects.",
    },
    {
        id: 5,
        icon: <FaPeopleCarry />,
        title: "Strong Teamwork",
        description: "Be part of a collaborative and supportive work environment where teamwork drives success in every project.",
    },
    {
        id: 6,
        icon: <FaArrowUp />,
        title: "Growth Potential",
        description: "Advance in your career with opportunities to specialize in construction management, site supervision, and advanced trades.",
    },
];

const positions = [
    {
        id: 1,
        image: images.position1,
        title: 'Construction Worker',
        content: 'We are looking for a hardworking and reliable Construction Worker to join our team. You will assist in building, repairing, and maintaining structures and infrastructure while ensuring high-quality standards are met.',
        details: {
            location: 'Company, City',
            timing: 'Full Time',
            hours: 'Day & Night Shifts Available',
            experience: 'Minimum 1 Year in Construction',
            position: 'Multiple Openings',
            days: 'Monday - Saturday',
            salary: '$50K - $70K',
            deadline: 'Apply Now - Open Until Filled',
            number: '123-456-7890',
        },
        overviews: [
            'We are looking for a hardworking and reliable Construction Worker to join our team. As a Construction Worker, you will assist in building, repairing, and maintaining structures and infrastructure. If you have experience in construction work and are committed to delivering high-quality results, we encourage you to apply.',
        ],
        responsibilities: [
            'Assisting in various construction tasks, including framing, masonry, and concrete work.',
            'Operating hand and power tools safely and efficiently.',
            'Loading and unloading construction materials and tools.',
            'Preparing and cleaning job sites before and after projects.',
            'Following safety guidelines and ensuring a hazard-free work environment.',
        ],
        qualifications: [
            'Previous experience in construction or a related field (preferred but not required).',
            'Ability to operate construction tools and machinery.',
            'Strong teamwork and communication skills.',
            'Good physical stamina and ability to lift heavy materials.',
            'Commitment to workplace safety and adherence to regulations.',
        ],
    },
    {
        id: 2,
        image: images.position2,
        title: 'Site Supervisor',
        content: 'We’re seeking an experienced Site Supervisor to oversee daily operations at our construction sites, ensuring that projects are completed safely, on time, and within budget.',
        details: {
            location: 'Company, City',
            timing: 'Full Time',
            hours: 'Standard Working Hours',
            experience: 'Minimum 3 Years in Site Supervision',
            position: '1 Position Available',
            days: 'Monday - Saturday',
            salary: '$60K - $80K',
            deadline: 'Apply Now - Open Until Filled',
            number: '123-456-7890',
        },
        overviews: [
            'As a Site Supervisor, you will coordinate and supervise construction workers and subcontractors on-site. Your role includes monitoring progress, ensuring safety standards, and resolving any on-site issues that may arise.',
        ],
        responsibilities: [
            'Supervise on-site operations and daily construction activities.',
            'Coordinate with subcontractors, suppliers, and labor teams.',
            'Ensure compliance with safety regulations and quality standards.',
            'Monitor project schedules and report progress to management.',
            'Resolve issues related to equipment, labor, or materials on-site.',
        ],
        qualifications: [
            'Proven experience as a Site Supervisor or similar role.',
            'Strong leadership and team management skills.',
            'Excellent communication and problem-solving abilities.',
            'In-depth understanding of construction procedures and safety standards.',
            'Relevant certifications or training in site supervision are a plus.',
        ],
    },
    {
        id: 3,
        image: images.position3,
        title: 'Heavy Equipment Operator',
        content: "Join our construction team as a skilled Heavy Equipment Operator. You'll be responsible for operating machinery such as bulldozers, excavators, and loaders to assist with various building and infrastructure projects.",
        details: {
            location: 'Company, City',
            timing: 'Full Time',
            hours: 'Day & Night Shifts Available',
            experience: 'Minimum 2 Years Operating Heavy Machinery',
            position: 'Multiple Openings',
            days: 'Monday - Saturday',
            salary: '$55K - $75K',
            deadline: 'Apply Now - Open Until Filled',
            number: '123-456-7890',
        },
        overviews: [
            'As a Heavy Equipment Operator, your role is vital in ensuring projects are completed efficiently and safely. You’ll be working with a variety of machines to excavate, move, and grade materials for construction and infrastructure development.',
        ],
        responsibilities: [
            'Operate heavy equipment such as excavators, bulldozers, and backhoes.',
            'Follow safety protocols and conduct equipment inspections.',
            'Load and unload materials on construction sites.',
            'Assist with job site preparation and clean-up.',
            'Report equipment issues or needed repairs promptly.',
        ],
        qualifications: [
            'Proven experience operating heavy equipment in construction.',
            'Valid certification or license for operating machinery.',
            'Strong awareness of site safety practices.',
            'Ability to read and follow project blueprints and instructions.',
            'Physical fitness and stamina for demanding tasks.',
        ],
    },
    {
        id: 4,
        image: images.position4,
        title: 'Civil Engineer',
        content: 'We’re seeking an experienced and detail-oriented Civil Engineer to oversee and manage construction projects from design through completion. Join our team and help shape infrastructure that lasts.',
        details: {
            location: 'Company, City',
            timing: 'Full Time',
            hours: 'Standard Working Hours',
            experience: 'Minimum 3 Years in Civil Engineering',
            position: 'Multiple Openings',
            days: 'Monday - Saturday',
            salary: '$75K - $100K',
            deadline: 'Apply Now - Open Until Filled',
            number: '123-456-7890',
        },
        overviews: [
            'As a Civil Engineer, you will be responsible for planning, designing, and supervising construction projects such as roads, buildings, and bridges. Your technical expertise and leadership will ensure project quality, efficiency, and regulatory compliance.',
        ],
        responsibilities: [
            'Design and develop construction plans based on technical specifications.',
            'Manage project budgets, timelines, and resources.',
            'Conduct site inspections and ensure compliance with safety regulations.',
            'Collaborate with contractors, architects, and other engineers.',
            'Prepare reports, feasibility studies, and cost estimates.',
        ],
        qualifications: [
            'Bachelor’s degree in Civil Engineering or related field.',
            'Professional Engineer (PE) license preferred.',
            'Strong analytical and problem-solving skills.',
            'Experience with CAD software and project management tools.',
            'Excellent communication and leadership abilities.',
        ],
    },
    {
        id: 5,
        image: images.position5,
        title: 'Construction Project Manager',
        content: 'We are seeking a highly organized Construction Project Manager to lead and oversee building projects from conception to completion. If you’re a strategic thinker with construction experience, we’d love to hear from you.',
        details: {
            location: 'Company, City',
            timing: 'Full Time',
            hours: 'Standard Working Hours',
            experience: 'Minimum 3 Years in Project Management',
            position: 'Multiple Openings',
            days: 'Monday - Saturday',
            salary: '$85K - $110K',
            deadline: 'Apply Now - Open Until Filled',
            number: '123-456-7890',
        },
        overviews: [
            'As a Construction Project Manager, you will be responsible for coordinating all phases of construction projects, ensuring work is completed on time, within budget, and to specified quality standards. You’ll lead cross-functional teams and maintain client communication throughout the project lifecycle.',
        ],
        responsibilities: [
            'Plan, coordinate, and manage construction projects from start to finish.',
            'Create and manage project timelines, budgets, and schedules.',
            'Communicate with clients, subcontractors, and team members.',
            'Ensure compliance with safety and building regulations.',
            'Oversee procurement of materials and management of resources.',
        ],
        qualifications: [
            'Bachelor’s degree in Construction Management, Civil Engineering, or related field.',
            'Proven experience in construction project management.',
            'Strong leadership and organizational skills.',
            'Excellent communication and negotiation abilities.',
            'Proficiency with project management software and tools.',
        ],
    }

];

export { positions, benefits, careerIntro };